<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1120"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    scrollable
  >
    <v-card class="plantillas">
      <v-toolbar color="#00A548" dark dense flat class="flex-grow-0">
        <v-toolbar-title class="text-subtitle-1 font-weight-medium">Plantillas {{ nombreEscuela }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn small depressed color="white" class="green--text text--darken-3 mr-1" @click="abrirEditor()">
          <v-icon left small>mdi-plus</v-icon>Nueva
        </v-btn>
        <v-btn icon aria-label="Cerrar" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <div class="plantillas__filtros">
        <v-text-field
          v-model="buscar"
          placeholder="Buscar por nombre o texto"
          prepend-inner-icon="mdi-magnify"
          hide-details
          dense
          filled
          clearable
          class="plantillas__buscar"
        ></v-text-field>
        <v-chip-group v-model="filtroTipo" mandatory active-class="green darken-2 white--text" class="plantillas__tipos">
          <v-chip small value="todas">Todas</v-chip>
          <v-chip small :value="1">Chat</v-chip>
          <v-chip small :value="2">Meta</v-chip>
        </v-chip-group>
        <v-checkbox v-model="verInactivas" label="Ver inactivas" hide-details dense class="mt-0 pt-0"></v-checkbox>
      </div>

      <v-card-text class="plantillas__cuerpo">
        <div v-if="cargando" class="plantillas__grid">
          <v-skeleton-loader v-for="n in 6" :key="n" type="image, list-item-two-line"></v-skeleton-loader>
        </div>

        <div v-else-if="!plantillas.length" class="plantillas__vacio">
          <v-icon size="40" color="green darken-2">mdi-message-image-outline</v-icon>
          <p class="text-subtitle-1 font-weight-medium mb-1">Aún no hay plantillas de {{ nombreEscuela }}</p>
          <p class="text-body-2 mb-4">Guarda aquí los mensajes que se mandan seguido: el texto, la imagen y cada cuánto se usan.</p>
          <v-btn color="#00A548" dark depressed @click="abrirEditor()"><v-icon left>mdi-plus</v-icon>Crear la primera</v-btn>
        </div>

        <div v-else-if="!filtradas.length" class="plantillas__vacio">
          <p class="text-body-2 mb-3">Ninguna plantilla coincide con los filtros.</p>
          <v-btn small text color="green darken-2" @click="limpiarFiltros">Quitar filtros</v-btn>
        </div>

        <div v-else class="plantillas__grid">
          <article
            v-for="item in filtradas"
            :key="item.idplantillas"
            class="plantilla"
            :class="{ 'plantilla--inactiva': !item.activo }"
          >
            <div class="plantilla__chat">
              <div class="burbuja">
                <img v-if="item.imagen" :src="urlImagen(item.imagen)" :alt="`Imagen de ${item.titulo}`" class="burbuja__img" loading="lazy" />
                <div class="burbuja__texto burbuja__texto--corto" v-html="formatear(item.body)"></div>
              </div>
            </div>

            <div class="plantilla__info">
              <div class="plantilla__nombre" :title="item.titulo">{{ item.titulo }}</div>
              <div class="plantilla__etiquetas">
                <span class="etiqueta" :class="item.tipo == 2 ? 'etiqueta--meta' : 'etiqueta--chat'">{{ item.tipo == 2 ? 'Meta' : 'Chat' }}</span>
                <span class="etiqueta"><v-icon x-small class="mr-1">mdi-calendar-sync</v-icon>{{ textoRecurrencia(item.recurrencia) }}</span>
                <span v-if="!item.activo" class="etiqueta etiqueta--inactiva">Inactiva</span>
              </div>
            </div>

            <div class="plantilla__acciones">
              <v-btn small text color="green darken-3" @click="copiar(item)">
                <v-icon left small>mdi-content-copy</v-icon>Copiar texto
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn icon small aria-label="Editar" @click="abrirEditor(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
              <v-menu left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small aria-label="Más opciones" v-bind="attrs" v-on="on"><v-icon small>mdi-dots-vertical</v-icon></v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="cambiarActivo(item)">
                    <v-list-item-title>{{ item.activo ? 'Desactivar' : 'Activar' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="porEliminar = item">
                    <v-list-item-title class="error--text">Eliminar</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </article>
        </div>
      </v-card-text>
    </v-card>

    <!-- Editor -->
    <v-dialog v-model="editor" max-width="920" :fullscreen="$vuetify.breakpoint.xsOnly" persistent scrollable>
      <v-card v-if="form">
        <v-toolbar flat dense class="flex-grow-0 editor__barra">
          <v-toolbar-title class="text-subtitle-1 font-weight-medium">{{ form.idplantillas ? 'Editar plantilla' : 'Nueva plantilla' }} · {{ nombreEscuela }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon aria-label="Cerrar" :disabled="guardando" @click="editor = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="editor">
          <v-form ref="form" class="editor__form" @submit.prevent="guardar">
            <v-text-field
              v-model="form.titulo"
              label="Nombre de la plantilla"
              :rules="[r.nombre]"
              counter="100"
              filled
              dense
            ></v-text-field>

            <div class="editor__label">Tipo</div>
            <v-btn-toggle v-model="form.tipo" mandatory dense color="green darken-3" class="mb-2">
              <v-btn :value="1" small>Chat</v-btn>
              <v-btn :value="2" small>Meta (aprobada)</v-btn>
            </v-btn-toggle>
            <p class="editor__ayuda">
              <template v-if="form.tipo == 1">Se manda dentro de una conversación activa (el cliente escribió en las últimas 24 horas).</template>
              <template v-else>Plantilla aprobada por Meta en Twilio. Es la única que se puede mandar a leads sin conversación activa.</template>
            </p>

            <v-text-field
              v-if="form.tipo == 2"
              v-model="form.sid"
              label="SID de la plantilla en Twilio"
              placeholder="HX..."
              hint="Cópialo de Twilio, en Content Template Builder"
              persistent-hint
              :rules="[r.sid]"
              filled
              dense
              class="mb-3"
            ></v-text-field>

            <v-textarea
              v-model="form.body"
              label="Texto"
              :rules="[r.texto]"
              :counter="maxTexto"
              :hint="form.tipo == 2 ? 'Escríbelo igual que la plantilla aprobada; {{1}} marca las variables' : 'Usa *negritas*, _cursivas_ y ~tachado~ como en WhatsApp'"
              persistent-hint
              auto-grow
              rows="5"
              filled
              dense
              class="mb-3"
            ></v-textarea>

            <div class="editor__label">Imagen</div>
            <div class="editor__imagen">
              <v-file-input
                v-model="archivo"
                accept="image/jpeg,image/png,image/webp"
                :label="form.imagen || vistaPrevia ? 'Cambiar imagen' : 'Subir imagen (opcional)'"
                prepend-icon=""
                prepend-inner-icon="mdi-image-plus"
                :rules="[r.imagen]"
                hint="JPG, PNG o WEBP de hasta 5 MB"
                persistent-hint
                filled
                dense
                show-size
              ></v-file-input>
              <v-btn v-if="form.imagen || vistaPrevia" small text color="error" class="mt-2" @click="quitarImagen">Quitar imagen</v-btn>
            </div>

            <div class="editor__fila">
              <v-select
                v-model="form.recurrencia"
                :items="recurrencias"
                label="Recurrencia"
                hint="Cada cuánto se usa"
                persistent-hint
                filled
                dense
              ></v-select>
              <v-switch v-model="form.activo" :true-value="1" :false-value="0" label="Activa" color="green darken-2" inset hide-details class="mt-2"></v-switch>
            </div>
          </v-form>

          <aside class="editor__preview" aria-label="Vista previa">
            <div class="editor__label">Vista previa</div>
            <div class="plantilla__chat plantilla__chat--grande">
              <div class="burbuja">
                <img v-if="imagenEditor" :src="imagenEditor" alt="Imagen de la plantilla" class="burbuja__img" />
                <div v-if="form.body" class="burbuja__texto" v-html="formatear(form.body)"></div>
                <div v-else class="burbuja__texto burbuja__texto--vacio">El texto aparecerá aquí</div>
              </div>
            </div>
          </aside>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text :disabled="guardando" @click="editor = false">Cancelar</v-btn>
          <v-btn color="#00A548" dark depressed :loading="guardando" @click="guardar">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmar eliminación -->
    <v-dialog :value="!!porEliminar" max-width="400" @input="porEliminar = null">
      <v-card v-if="porEliminar">
        <v-card-title class="text-subtitle-1 font-weight-medium">¿Eliminar «{{ porEliminar.titulo }}»?</v-card-title>
        <v-card-text>Deja de aparecer en el catálogo. Si solo quieres dejar de usarla por un tiempo, mejor desactívala.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="guardando" @click="porEliminar = null">Cancelar</v-btn>
          <v-btn color="error" depressed :loading="guardando" @click="eliminar">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="aviso.ver" timeout="2500" color="green darken-3" bottom>{{ aviso.texto }}</v-snackbar>
    <Alerta v-if="respuestaAlerta" :parametros="parametros" />
  </v-dialog>
</template>

<script>
  import Alerta         from '@/components/alertas/Alerta.vue'
  import validarErrores from '@/mixins/validarErrores'

  const MAX_TEXTO  = { 1: 1600, 2: 1024 }
  const MAX_IMAGEN = 5 * 1024 * 1024

  const RECURRENCIAS = [
    { value: 'unica',        text: 'Una sola vez' },
    { value: 'diaria',       text: 'Diaria' },
    { value: 'semanal',      text: 'Semanal' },
    { value: 'quincenal',    text: 'Quincenal' },
    { value: 'mensual',      text: 'Mensual' },
    { value: 'inicio_ciclo', text: 'Cada inicio de ciclo' },
  ]

  const escapar = (t) => String(t || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

  export default {
    name: 'PlantillasCatalogo',

    components: { Alerta },

    mixins: [ validarErrores ],

    props: {
      value:   { type: Boolean, default: false },
      escuela: { type: Number, required: true },
    },

    data: () => ({
      plantillas: [],
      cargando: false,
      guardando: false,
      buscar: '',
      filtroTipo: 'todas',
      verInactivas: false,
      editor: false,
      form: null,
      archivo: null,
      vistaPrevia: null,
      porEliminar: null,
      aviso: { ver: false, texto: '' },
      recurrencias: RECURRENCIAS,
    }),

    computed: {
      nombreEscuela () {
        return this.escuela == 2 ? 'FAST' : 'INBI'
      },

      filtradas () {
        const q = String(this.buscar || '').trim().toLowerCase()
        return this.plantillas.filter((p) =>
          (this.verInactivas || p.activo) &&
          (this.filtroTipo === 'todas' || p.tipo == this.filtroTipo) &&
          (!q || `${p.titulo} ${p.body}`.toLowerCase().includes(q))
        )
      },

      maxTexto () {
        return MAX_TEXTO[this.form ? this.form.tipo : 1]
      },

      imagenEditor () {
        if (this.vistaPrevia) return this.vistaPrevia
        return this.form && this.form.imagen ? this.urlImagen(this.form.imagen) : null
      },

      r () {
        return {
          nombre: (v) => (String(v || '').trim().length >= 3 && String(v).trim().length <= 100) || 'Entre 3 y 100 caracteres',
          sid:    (v) => /^HX[0-9a-f]{32}$/i.test(String(v || '').trim()) || 'HX seguido de 32 caracteres',
          texto:  (v) => {
            const t = String(v || '').trim()
            if (!t) return 'Escribe el texto'
            return t.length <= this.maxTexto || `Máximo ${this.maxTexto} caracteres`
          },
          imagen: (f) => !f || f.size <= MAX_IMAGEN || 'La imagen no puede pesar más de 5 MB',
        }
      },
    },

    watch: {
      value: {
        immediate: true,
        handler (abierto) { if (abierto) this.cargarPlantillas() },
      },

      archivo (f) {
        if (this.vistaPrevia) URL.revokeObjectURL(this.vistaPrevia)
        this.vistaPrevia = f ? URL.createObjectURL(f) : null
      },

      editor (abierto) {
        if (!abierto) this.archivo = null
      },
    },

    beforeDestroy () {
      if (this.vistaPrevia) URL.revokeObjectURL(this.vistaPrevia)
    },

    methods: {
      cargarPlantillas () {
        this.cargando = true
        return this.$http.get('twilio.plantillas.catalogo', { params: { escuela: this.escuela } }).then((response) => {
          this.plantillas = response.data
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        }).finally(() => { this.cargando = false })
      },

      urlImagen (nombre) {
        const api = String(this.$http.defaults.baseURL || '').replace(/\/?$/, '/')
        return `${api}imageneswhatsapplocal/${nombre}`
      },

      textoRecurrencia (valor) {
        const r = RECURRENCIAS.find((x) => x.value === valor)
        return r ? r.text : 'Una sola vez'
      },

      // Formato de WhatsApp sobre texto ya escapado: *negrita*, _cursiva_, ~tachado~ y variables {{1}}.
      formatear (texto) {
        return escapar(texto)
          .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
          .replace(/(^|[\s(])_([^_\n]+)_/g, '$1<em>$2</em>')
          .replace(/~([^~\n]+)~/g, '<s>$1</s>')
          .replace(/\{\{(\d+)\}\}/g, '<span class="burbuja__variable">{{$1}}</span>')
      },

      limpiarFiltros () {
        this.buscar = ''
        this.filtroTipo = 'todas'
        this.verInactivas = true
      },

      abrirEditor (item) {
        this.form = item
          ? { ...item, sid: item.sid || '' }
          : { idplantillas: 0, titulo: '', tipo: 1, body: '', sid: '', imagen: null, recurrencia: 'unica', activo: 1 }
        this.form.quitar_imagen = 0
        this.archivo = null
        this.editor = true
        this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
      },

      quitarImagen () {
        this.archivo = null
        this.form.imagen = null
        this.form.quitar_imagen = 1
      },

      guardar () {
        if (!this.$refs.form.validate()) return

        const datos = new FormData()
        ;['titulo', 'body', 'tipo', 'recurrencia', 'activo', 'quitar_imagen'].forEach((k) => datos.append(k, this.form[k]))
        datos.append('escuela', this.escuela)
        datos.append('sid', this.form.tipo == 2 ? String(this.form.sid || '').trim() : '')
        if (this.archivo) datos.append('imagen', this.archivo)

        const id = this.form.idplantillas
        const peticion = id
          ? this.$http.put(`twilio.plantillas.catalogo/${id}`, datos)
          : this.$http.post('twilio.plantillas.catalogo', datos)

        this.guardando = true
        return peticion.then((response) => {
          this.reemplazar(response.data)
          this.editor = false
          this.avisar(id ? 'Plantilla actualizada' : 'Plantilla creada')
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        }).finally(() => { this.guardando = false })
      },

      cambiarActivo (item) {
        const activo = item.activo ? 0 : 1
        return this.$http.put(`twilio.plantillas.catalogo/${item.idplantillas}`, { activo }).then((response) => {
          this.reemplazar(response.data)
          this.avisar(activo ? 'Plantilla activada' : 'Plantilla desactivada')
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        })
      },

      eliminar () {
        const item = this.porEliminar
        this.guardando = true
        return this.$http.delete(`twilio.plantillas.catalogo/${item.idplantillas}`).then(() => {
          this.plantillas = this.plantillas.filter((p) => p.idplantillas !== item.idplantillas)
          this.porEliminar = null
          this.avisar('Plantilla eliminada')
        }).catch((error) => {
          this.validarError(error.response ? error.response.data.message : error.message)
        }).finally(() => { this.guardando = false })
      },

      reemplazar (plantilla) {
        const i = this.plantillas.findIndex((p) => p.idplantillas === plantilla.idplantillas)
        if (i >= 0) this.plantillas.splice(i, 1, plantilla)
        else this.plantillas.unshift(plantilla)
      },

      copiar (item) {
        if (!navigator.clipboard) return this.validarError('Tu navegador no permite copiar automáticamente')
        navigator.clipboard.writeText(item.body)
          .then(() => this.avisar('Texto copiado, ya lo puedes pegar en el chat'))
          .catch(() => this.validarError('No se pudo copiar el texto'))
      },

      avisar (texto) {
        this.aviso = { ver: true, texto }
      },
    },
  }
</script>

<style scoped>
  .plantillas {
    --chat-fondo: #efeae2;
    --chat-burbuja: #d9fdd3;
    --chat-texto: #111b21;
    --chat-tenue: #667781;
    --linea: #e3e6e8;
  }

  .plantillas__filtros {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--linea);
  }

  .plantillas__buscar {
    flex: 1 1 260px;
    max-width: 380px;
  }

  .plantillas__tipos {
    flex: 0 0 auto;
  }

  .plantillas__cuerpo {
    padding: 16px !important;
    background: #f6f7f8;
    min-height: 320px;
  }

  .plantillas__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 16px;
  }

  .plantillas__vacio {
    max-width: 380px;
    margin: 48px auto;
    text-align: center;
    color: var(--chat-tenue);
  }

  .plantillas__vacio .text-subtitle-1 {
    color: var(--chat-texto);
  }

  .plantilla {
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(17, 27, 33, 0.08);
    overflow: hidden;
    transition: box-shadow 0.18s ease-out;
  }

  .plantilla:hover {
    box-shadow: 0 6px 18px rgba(17, 27, 33, 0.12);
  }

  .plantilla--inactiva .plantilla__chat {
    filter: grayscale(1);
    opacity: 0.6;
  }

  .plantilla__chat {
    background: var(--chat-fondo);
    padding: 14px 12px;
    display: flex;
    justify-content: flex-end;
    flex: 1 1 auto;
  }

  .plantilla__chat--grande {
    border-radius: 12px;
    min-height: 240px;
    align-items: flex-start;
  }

  .burbuja {
    max-width: 92%;
    background: var(--chat-burbuja);
    color: var(--chat-texto);
    border-radius: 8px 0 8px 8px;
    padding: 4px;
    box-shadow: 0 1px 1px rgba(11, 20, 26, 0.13);
  }

  .burbuja__img {
    display: block;
    width: 100%;
    max-height: 220px;
    object-fit: cover;
    border-radius: 6px;
  }

  .plantilla__chat--grande .burbuja__img {
    max-height: 320px;
    object-fit: contain;
    background: rgba(0, 0, 0, 0.04);
  }

  .burbuja__texto {
    padding: 6px 8px 4px;
    font-size: 14px;
    line-height: 1.45;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .burbuja__texto--corto {
    display: -webkit-box;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .burbuja__texto--vacio {
    color: var(--chat-tenue);
    font-style: italic;
  }

  .burbuja >>> .burbuja__variable {
    background: rgba(0, 165, 72, 0.16);
    border-radius: 4px;
    padding: 0 3px;
    font-weight: 500;
  }

  .plantilla__info {
    padding: 10px 14px 4px;
  }

  .plantilla__nombre {
    font-weight: 600;
    font-size: 14px;
    color: var(--chat-texto);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .plantilla__etiquetas {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  }

  .etiqueta {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    line-height: 18px;
    padding: 0 8px;
    border-radius: 9px;
    background: #eef0f1;
    color: #3b4a54;
  }

  .etiqueta--chat {
    background: #dff3e6;
    color: #0b6b34;
  }

  .etiqueta--meta {
    background: #e3ecfb;
    color: #1d4fa3;
  }

  .etiqueta--inactiva {
    background: #fbe7e7;
    color: #a12a2a;
  }

  .plantilla__acciones {
    display: flex;
    align-items: center;
    padding: 2px 6px 6px 4px;
  }

  .editor__barra {
    border-bottom: 1px solid var(--linea, #e3e6e8);
  }

  .editor {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 340px);
    gap: 24px;
    padding: 20px !important;
  }

  .editor__label {
    font-size: 12px;
    font-weight: 600;
    color: #3b4a54;
    margin-bottom: 6px;
  }

  .editor__ayuda {
    font-size: 12px;
    color: #667781;
    margin-bottom: 16px;
  }

  .editor__imagen {
    margin-bottom: 12px;
  }

  .editor__fila {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .editor__fila .v-select {
    flex: 1 1 auto;
  }

  .editor__preview {
    position: sticky;
    top: 0;
    align-self: start;
  }

  .editor__preview .plantilla__chat {
    --chat-fondo: #efeae2;
    --chat-burbuja: #d9fdd3;
    --chat-texto: #111b21;
    --chat-tenue: #667781;
  }

  @media (max-width: 760px) {
    .editor {
      grid-template-columns: minmax(0, 1fr);
    }

    .editor__preview {
      position: static;
      order: -1;
    }

    .plantilla__chat--grande {
      min-height: 0;
    }
  }
</style>
